/**
 * The words on Imagine Yourself There (well/product/33), kept out of the
 * screen so a test can hold them to the first-screen rules (intro-copy.ts
 * BANNED_WORDS: no "AI", no "generate", no "prompt"…; no exclamation marks).
 *
 * The owner's brief says to show "how the photo gets turned into AI". The word
 * stays off the screen on purpose: research 22 found the AI fear strongest in
 * exactly the people these ads reach. The transformation is shown as a picture
 * (components/PhotoToDrawing.tsx) and said as "drawn" and "the crew draw you".
 */

import { yourKind, KINDS } from './intro-copy';
import { ROLL_WHO, SHAPE_FOR, shapeById, type RollShape } from './idea-shapes';

export { ROLL_WHO, SHAPES, shapeById, type RollShape } from './idea-shapes';

/** The exercise this is, in the data (scripts/import-exercises.py IMAGINE_YOURSELF_THERE). */
export const IMAGINE = 'imagine-yourself-there';

/**
 * The example, as the whole formula (owner, 2026-10-14): a photo of you + a
 * place = you, drawn by the crew, in that place. The result is a real one from
 * Kindling's own pipeline (the owner's selfie through the local app, no
 * mocks), so it shows what people actually get. `place` is the place that
 * picture was drawn in, and must stay so.
 */
export const EXAMPLE = {
  photo: '/art/imagine/example-photo.jpg',
  result: '/art/imagine/example-result.jpg',
  place: 'A lighthouse made of cake',
  /** above the sum, so "Your photo" over the owner's photo reads as an example, not as where yours goes */
  kicker: 'For example',
  photoLabel: 'Your photo',
  placeLabel: 'Your place',
  resultLabel: 'Your first creation',
  /** what the whole equation says, for a screen reader */
  said: 'Your photo, plus your place, makes your first creation: you, drawn by the crew, in a lighthouse made of cake.',
  /** the same sum for the intro's idea step (`idea_step`): the postcard is an idea now, the picture the same */
  ideaLabel: 'Your idea',
  ideaSaid: 'Your photo, plus your idea, makes your first creation: you, drawn by the crew, inside the world of your idea.',
};

export const PLACE = {
  tag: 'Imagine',
  label: 'First, a place',
  title: 'Dream up a place you’d like to be.',
  body: 'Real, made up, or a bit of both. Nothing here can be wrong.',
  placeholder: 'A lighthouse made of cake',
  sparksLabel: 'Stuck? Take one:',
  again: 'Deal three more',
  done: 'That’s my place',
};

export const PHOTO = {
  title: 'Make your first creation.',
  /** the sum, as a sentence; on screen the example's captions say it, one under each picture (doc 33 §3) */
  line: 'Your photo + your place = your first creation.',
  /** {place} is theirs, as typed */
  body: 'Add a photo of you, and the crew will draw you in {place}.',
  /** after the idea roll, under the crew's sketch of it: the stand-in in it is where they go */
  rollBody: 'That plain figure is you. Add a photo, and the crew will put the real you in it.',
  /** after the idea roll, in place of `skip`: no photo, and the sketch stays as it is, the plain figure in it */
  rollSkip: 'Just leave it as is',
  /** while the sketch is being drawn */
  sketching: 'Developing',
  /** the sum's middle, after the idea roll */
  sketchLabel: 'Your idea',
  /** with an idea from the intro: {yours} is "your novel" (intro-copy yourKind) */
  ideaBody: 'Add a photo of you, and the crew will draw you inside the world of {yours}.',
  privacy: 'Only the crew sees your photo. They draw you from it, and it is never posted.',
  done: 'Make my first creation',
  skip: 'No photo, draw a character instead',
};

export const LESSON = {
  line: 'Allow yourself to imagine.',
  more: 'You made up a place and put yourself in it. Every story starts like that.',
  /**
   * After the idea roll, whose idea may be a podcast or an app, not a place:
   * their goal said back to them (owner, 2026-10-29), {yours} as the roll's
   * goal says it ("your podcast"); moreRollBare with no goal to say.
   */
  moreRoll: 'A great idea for {yours}, and you imagined it. You can create more goals and dreams later. Dreaming is creating.',
  moreRollBare: 'A great idea, and you imagined it. You can create more goals and dreams later. Dreaming is creating.',
  done: 'Next',
  /** the darkroom's bubble straight after the idea roll, where the lesson screen was (lean Day 1, 2026-10-30) */
  making: 'Allow yourself to imagine',
  /** under it: "Make it" turned the crew on, so say so, and where the switch is */
  crewNote: 'Your crew draw from what you make. You can turn them off under Friends.',
};

/**
 * Leaving the lesson for the darkroom, when the post is not kept yet. The email
 * that used to sit here is asked after the print now, as how you get back
 * tomorrow (data/keep-copy.ts).
 */
export const KEEPING = {
  /** while the post is still being kept, after a tap */
  waiting: 'One moment',
  failed: 'That didn’t save. Try again.',
};

/**
 * The road, shown in the intro for this starter after the first card
 * (owner, 2026-10-14/15): today's stop is their first creation. No heading:
 * the map is the screen.
 */
export const ROAD = {
  body: 'A few minutes of creation each day, one stop at a time. Today, your first creation.',
  /** today's stop, as the map labels it */
  stop: 'Your first creation',
  done: 'Make your first creation',
  /** under the button on the road landing, where there is no Back: what it costs */
  minutes: 'Takes {n} minutes. Nothing to install.',
  /** on the landing's road (components/DepthRoad.tsx): under today's stop (then its kind and minutes), over tomorrow's sealed one, and the goal at the end, the ad's own 21 days */
  today: 'Today',
  tomorrow: 'Tomorrow',
  goalDay: 'Day 21',
};

/** The darkroom, the print and the reveal, said for this starter (screens/Reward.tsx). */
export const REVEAL = {
  developing: 'Your first creation is developing',
  /** the darkroom, for a screen reader */
  label: 'Your first creation is developing',
  dry: 'Your first creation is dry.',
  /** under the picture, once it is turned over */
  credit: (day: number) => `Your first creation · drawn by the Moon · Day ${day}`,
};

/** What the print says while it develops, and on the peg: their place, with them in it. */
export function printWords(place: string, photo: boolean): string {
  return `${photo ? 'You' : 'Someone'}, in ${lower(place)}`;
}

/** The print's words for the idea path: "You, in the world of your novel" (the backend's ideaWords says the same). */
export function ideaWords(kind: string, photo: boolean): string {
  return `${photo ? 'You' : 'Someone'}, in the world of ${yourKind(kind)}`;
}

/** "A treehouse run by cats" reads as "you, in a treehouse run by cats". Leaves "I" and names alone. */
export function lower(place: string): string {
  const p = place.trim().replace(/[.\s]+$/, '');
  return /^(A|An|The|My|Your|Our)\b/.test(p) ? p.charAt(0).toLowerCase() + p.slice(1) : p;
}

/**
 * The sparks: never a blank page. Each is a place and a twist that do not
 * belong together, the same move as Ten Bad Ideas' dealt situation, so the
 * first thing on the screen is already a little silly and nothing anyone types
 * can be worse. A spark drops into the box, where it can be changed.
 */
export const SPARK_PLACES = [
  'A lighthouse', 'A treehouse', 'A bakery', 'A library', 'A rooftop garden', 'A diner', 'A tiny boat',
  'A cinema', 'A greenhouse', 'A castle', 'A night train', 'A beach hut', 'A bookshop', 'A swimming pool',
];
export const SPARK_TWISTS = [
  'on the moon', 'made of cake', 'under the sea', 'in the clouds', 'that never closes', 'run by cats',
  'the size of a teacup', 'where it always snows', 'at the top of a mountain', 'that floats',
  'inside a snow globe', 'in the middle of a desert', 'full of fireflies', 'in your old school',
];

/** Three sparks, no place and no twist twice, none of `avoid` (the ones just shown). `rand` is for tests. */
export function dealSparks(avoid: string[] = [], rand: () => number = Math.random, n = 3): string[] {
  const pick = <T,>(xs: T[]) => xs[Math.floor(rand() * xs.length)];
  const out: string[] = [];
  const usedP = new Set<string>();
  const usedT = new Set<string>();
  for (let tries = 0; out.length < n && tries < 200; tries++) {
    const p = pick(SPARK_PLACES.filter((x) => !usedP.has(x)));
    const t = pick(SPARK_TWISTS.filter((x) => !usedT.has(x)));
    const s = `${p} ${t}`;
    if (avoid.includes(s)) continue;
    usedP.add(p); usedT.add(t); out.push(s);
  }
  return out;
}

/** Fill {key} from `vals`. */
export function fillIn(s: string, vals: Record<string, string>): string {
  return s.replace(/\{(\w+)\}/g, (m, k) => vals[k] ?? m);
}

// ── The idea roll (well/product/33 §9, the `place_step` experiment's `machine` arm) ──

/**
 * The place step as a game of levels, and the answer to "I don't have any" on
 * the intro's idea question. One sentence with three blanks that say what
 * goes in them: [who] at [where], [doing what]. You are not in the words: you
 * are the plain figure on the postcard, and later the photo.
 *
 * Level 1 is dice only. Level 2 opens every row for writing: the doing row
 * (MUST, what is happening in it) comes up empty and is the one they have to
 * write; the other two keep their rolls as words they may change or leave.
 * "Make it", there, goes to "Make your first creation" and the crew's sketch
 * of it (kindling-sketch.service). Owner, 2026-10-30, after W4's replays
 * showed people stopping on Level 3's second row to write: only the what is
 * a must, so the rest of the levels went. Once anything is rolled, a small
 * "skip" goes there too, with the rolls standing in for anything not yet
 * written, so nobody is ever stuck.
 *
 * The sentence's shape follows the dream and the kind they just picked
 * (data/idea-shapes.ts): a podcast is "Me and [co-host] talking about [what]
 * every [when]". Anything they write is drawn on the
 * postcard as a present still wrapped, which the crew's drawing opens.
 *
 * 2026-10-27, owner, twice: first replacing the Postcard Machine (too hard,
 * the hardest word left to them with no part of speech, no link to the
 * question before), then this: no headline, a path on screen the whole time,
 * no typing before it is unlocked, no "Use this one" in the way, one "Make it"
 * at the end, and the good quick picture after it.
 */
export const ROLL = {
  /** Level 1's line under the path, before the first roll; from "I don't have any" (screens/Intro.tsx), {yours} is "your memoir" (intro-copy yourKind) */
  noIdea: 'No idea yet? Let’s ideate together.',
  /** over everything, on every level: what this idea is for, from the kind they picked ({yours}: "your podcast") */
  goal: 'An idea for {yours}',
  /** …or, with no kind, from the dream they picked (intro-copy DREAMS ids); otherwise goalBare */
  goalForDream: { writer: 'your writing', artist: 'your art', filmmaker: 'your next film', influencer: 'your channel', business: 'your business', creator: 'your next project', dreamer: 'your daydreams' } as Record<string, string>,
  goalBare: 'Your next idea',
  start: 'Roll the dice to start.',
  /** Level 1, once the dice have landed */
  rolled: 'Like it? Or roll again.',
  /** Level 1, between dice: {slot} is what the next row's die deals (the shape's `noun`: "a co-host", "a day") */
  rollNextLine: 'Now roll {slot}.',
  /** a smaller line under it, on every level: where this is going (owner: "don't worry, the idea will become yours") */
  cue: { 1: 'Don’t worry: next, the idea becomes yours.', 2: 'Only this one is a must. The rest can stay rolled.' } as Record<number, string>,
  /** under the path, once the row the level unlocked is written */
  written: 'That’s yours now.',
  rollAll: 'Roll the dice',
  /** Level 1's big button, one die at a time: {slot} is what the row's die deals ("Roll a co-host") */
  rollThe: 'Roll {slot}',
  rollAgain: 'Roll again',
  rollOne: 'Roll {slot} again',
  /**
   * A tap on the words of a row that only rolls yet says what to do now, not
   * when that row opens (owner, 2026-10-28): {slot} is what a die deals in
   * nowRoll, a row's label in nowWrite (said too when "Make it" is tapped
   * before that row is written); {next} the way on's label.
   */
  nowRoll: 'Roll {slot} first',
  nowWrite: 'Write “{slot}” first',
  nowNext: 'Tap “{next}” first',
  /** The way on (owner, 2026-10-28): into Level 2, where writing starts. */
  nextIs: 'Next: Your turn',
  make: 'Make it',
  skip: 'skip',
  /** the path's stops */
  level: 'Level {n}',
  picture: 'Your first creation',
  /** the postcard, for a screen reader, while a present is wrapped */
  wrapped: 'a present, still wrapped',
};

export type RollSlot = 'who' | 'where' | 'doing';
export const ROLL_SLOTS: readonly RollSlot[] = ['who', 'where', 'doing'];
export const LEVELS = 2;
/** The one row they have to write before "Make it": what is happening (owner, 2026-10-30). */
export const MUST: RollSlot = 'doing';

export interface RollOption { text: string; art: string }

/** The shape a dream and the kind they picked (its label, as the profile keeps it) roll: data/idea-shapes.ts. */
export function shapeFor(dream?: string, kindLabel?: string): RollShape {
  const kind = dream && kindLabel ? KINDS[dream]?.find((k) => k.label === kindLabel.trim())?.id : undefined;
  return shapeById((kind && SHAPE_FOR[`${dream}:${kind}`]) || (dream && SHAPE_FOR[dream]) || 'anything');
}

function optionsFor(slot: RollSlot, shape: RollShape): readonly RollOption[] {
  return slot === 'who' ? ROLL_WHO : shape.options[slot];
}

/** The picture piece for a rolled part: its own, or (for words the dice never deal) none for a who, the shape's for the rest. */
export function artFor(slot: RollSlot, text: string, shape: RollShape = shapeById()): string | undefined {
  const own = optionsFor(slot, shape).find((o) => o.text === text)?.art;
  return own ?? (slot === 'who' ? undefined : shape.art[slot]);
}

/**
 * A blank: what the dice landed on, and what is written in it. `shown` is false
 * until the dice are first rolled (Level 1 opens on three question marks); a
 * row a level unlocks starts written with the roll, which counts as theirs once
 * they change it.
 */
export interface RollPart { rolled: string; mine: string; shown?: boolean }
export interface RollWords { who: string; where: string; doing: string }
export interface Roll {
  who: RollPart;
  where: RollPart;
  doing: RollPart;
  /** 1 to LEVELS: Level 1 is the dice, Level 2 the writing */
  level: number;
  /** the sentence shape (data/idea-shapes.ts), from the dream and the kind they picked */
  shape?: string;
  /** the dream (intro-copy DREAMS id), for the crew's sketch */
  dream?: string;
}

export const shapeOf = (r: Roll): RollShape => shapeById(r.shape);

/** The reel's next word: never the one showing, when there is a choice. `rand` is for tests. */
export function spinReel(list: readonly string[], current: string, rand: () => number = Math.random): string {
  const pool = list.length > 1 ? list.filter((x) => x !== current) : list;
  return pool[Math.min(pool.length - 1, Math.floor(rand() * pool.length))];
}

const texts = (slot: RollSlot, shape: RollShape) => optionsFor(slot, shape).map((o) => o.text);

/** Level 1, the dice not yet rolled (the first roll shows what they hold), in the shape their dream and kind call for. */
export function dealRoll(dream?: string, kindLabel?: string, rand: () => number = Math.random): Roll {
  const shape = shapeFor(dream, kindLabel);
  const part = (slot: RollSlot): RollPart => ({ rolled: spinReel(texts(slot, shape), '', rand), mine: '', shown: false });
  return { who: part('who'), where: part('where'), doing: part('doing'), level: 1, shape: shape.id, ...(dream ? { dream } : {}) };
}

export const PART_MAX = 60;

/** What they write, tidied as they type: one space between words, no edges in front, no line breaks, 60 characters. */
export function cleanPart(s: string): string {
  return s.replace(/[\r\n]+/g, ' ').replace(/\s+/g, ' ').replace(/^\s+/, '').slice(0, PART_MAX);
}

const hasWord = (s: string) => /[\p{L}\p{N}]/u.test(s);

/**
 * The rows in the order the sentence says them, which is also the order
 * Level 1's dice land in: who always first, then the podcast's topic before
 * its day, the game's setting before what you do in it (QA, 2026-10-28).
 */
export function slotOrder(shape: RollShape = shapeById()): readonly RollSlot[] {
  return sentenceParts(shape).filter((p): p is RollSlot => p === 'who' || p === 'where' || p === 'doing');
}

/** The rows a level lets them write: none on Level 1, every one after it. */
export function writableAt(level: number, shape?: RollShape): readonly RollSlot[] {
  return level > 1 ? slotOrder(shape) : [];
}
export const isWritable = (r: Roll, slot: RollSlot) => writableAt(r.level, shapeOf(r)).includes(slot);
/** The row they have to write on this level; none on Level 1. */
export const unlockedNow = (r: Roll): RollSlot | undefined => (r.level > 1 ? MUST : undefined);

/** Have the dice been rolled for this row yet (Level 1 opens on question marks)? */
export const isShown = (r: Roll, slot: RollSlot) => r[slot].shown !== false;

/**
 * Their own words in a blank: written where writing is unlocked, with a word
 * in it (a letter or a digit). In the row they must write, typing exactly
 * what the dice had dealt counts too: they chose it (QA, 2026-10-28). The
 * other rows open holding their rolls, so those are theirs once changed.
 */
export function isMine(r: Roll, slot: RollSlot): boolean {
  const said = cleanPart(r[slot].mine).trim();
  return isWritable(r, slot) && hasWord(said) && (slot === MUST || said !== r[slot].rolled);
}

/** What a blank says now: what is written in an unlocked row, else the roll once rolled; nothing before that. */
export function partWords(r: Roll, slot: RollSlot): string {
  if (isWritable(r, slot)) return cleanPart(r[slot].mine).trim();
  return isShown(r, slot) ? r[slot].rolled : '';
}

/** The sentence as it stands, the rolls standing in for anything not written yet (what "skip" goes on with). */
export const wordsOf = (r: Roll): RollWords => ({
  who: partWords(r, 'who') || r.who.rolled, where: partWords(r, 'where') || r.where.rolled, doing: partWords(r, 'doing') || r.doing.rolled,
});

/** Roll one blank again (the first roll of a row shows what it was dealt). A row that is theirs to write has no die. */
export function rollSlot(r: Roll, slot: RollSlot, rand: () => number = Math.random): Roll {
  if (isWritable(r, slot)) return r;
  return { ...r, [slot]: { rolled: isShown(r, slot) ? spinReel(texts(slot, shapeOf(r)), r[slot].rolled, rand) : r[slot].rolled, mine: '' } };
}

/** "Roll the dice": every row that still has a die. */
export function rollAll(r: Roll, rand: () => number = Math.random): Roll {
  return ROLL_SLOTS.reduce((acc, slot) => rollSlot(acc, slot, rand), r);
}

/** Writing in a row; a row not yet unlocked cannot be written in. */
export function writePart(r: Roll, slot: RollSlot, text: string): Roll {
  if (!isWritable(r, slot)) return r;
  return { ...r, [slot]: { ...r[slot], mine: cleanPart(text) } };
}

/** Level 1 once the dice have been rolled; Level 2 once the row they must write is theirs: on to make it. */
export const levelDone = (r: Roll) => (r.level === 1 ? ROLL_SLOTS.every((slot) => isShown(r, slot)) : isMine(r, MUST));

/** How one blank is drawn on the postcard (components/IdeaPostcard.tsx): what the dice dealt, a present of theirs, or one still to write. */
export function pieceOf(r: Roll, slot: RollSlot): { art?: string; mine?: boolean; blank?: boolean } {
  if (isMine(r, slot)) return { mine: true };
  // not rolled yet, or an unlocked row emptied: a question mark
  if (!partWords(r, slot)) return { blank: true };
  return { art: artFor(slot, r[slot].rolled, shapeOf(r)) };
}

/** How many of the three are their own words. */
export const typedCount = (r: Roll) => ROLL_SLOTS.filter((slot) => isMine(r, slot)).length;

/** Back a level, to change their mind: its row has its die again, and what they wrote waits in it for when they come back. */
export function prevLevel(r: Roll): Roll {
  return { ...r, level: Math.max(1, r.level - 1) };
}

/**
 * On to Level 2: the dice go, and the row they must write comes up empty, so
 * there is no clicking through it (owner, 2026-10-28); the other rows open
 * holding their rolls, to change or leave. Words of their own from an earlier
 * visit are still there.
 */
export function nextLevel(r: Roll): Roll {
  const n = { ...r, level: Math.min(LEVELS, r.level + 1) };
  if (n.level === 1) return n;
  for (const slot of ROLL_SLOTS) {
    const said = cleanPart(n[slot].mine).trim();
    const mine = slot === MUST ? (said === n[slot].rolled ? '' : n[slot].mine) : hasWord(said) ? n[slot].mine : n[slot].rolled;
    n[slot] = { ...n[slot], mine, shown: true };
  }
  return n;
}

/** A roll saved by the four-level roll (before 2026-10-30) comes back on Level 2, its words kept. */
export const restoreRoll = (r: Roll): Roll => (r.level > LEVELS ? nextLevel({ ...r, level: LEVELS - 1 }) : r);

/** Level 1's next die: the first row not yet rolled, in the sentence's order, so it is plain where to tap; none once all three are. */
export const nextToRoll = (r: Roll): RollSlot | undefined => (r.level === 1 ? slotOrder(shapeOf(r)).find((slot) => !isShown(r, slot)) : undefined);

/** The sentence in its pieces, for a caption that sets each blank apart: text, then a blank, in the shape's order. */
export function sentenceParts(shape: RollShape): (string | RollSlot)[] {
  return shape.template.split(/\{(who|where|doing)\}/).map((p, i) => (i % 2 ? (p as RollSlot) : p)).filter((p) => p !== '');
}

/** A where that already says how it is there: "in my kitchen", "on Sundays", "every week", "tonight". */
const LEADS = /^(in|at|on|by|to|from|under|underneath|inside|outside|near|behind|beside|next to|above|below|over|across|around|through|into|onto|between|beneath|beyond|up|down|along|among|every|each|once|whenever|when|after|before|during|all|this|tonight|today|tomorrow|always|daily|weekly|nightly|monthly|someday|somewhere|anywhere|everywhere|nowhere|here|there|downtown|upstairs|downstairs|underwater|abroad|outdoors|indoors|far)\b/i;

/**
 * A written where, made to fit the sentence now that the sentence has no "at"
 * of its own (QA, 2026-10-28: "at the middle of a forest", "at Brooklyn"):
 * one that says how it is there is left alone, else the shape's little word
 * goes in front ("Brooklyn" → "in Brooklyn"; the podcast's "Friday night" →
 * "every Friday night", "Sundays" → "on Sundays").
 */
export function leadWhere(where: string, shape: RollShape = shapeById()): string {
  const w = where.trim();
  const lead = shape.lead ?? 'in';
  if (!w || !lead || LEADS.test(w)) return w;
  if (lead === 'every' && /[^s']s$/i.test(w)) return `on ${w}`;
  if (lead === 'in') {
    // the place itself, before anything about it: "the laundromat" in "the laundromat on 5th street"
    const head = w.toLowerCase().split(/\s+(?:on|in|at|of|by|near|under|with|for|where|that|behind|beside)\s+/)[0].split(/\s+/).pop() ?? '';
    if (ON_PLACES.has(head)) return `on ${w}`;
    if (AT_PLACES.has(head)) return `at ${w}`;
  }
  return `${lead} ${w}`;
}
/** The few places "in" reads wrong for, by the place's own word: "on the beach", "at the bus stop". */
const ON_PLACES = new Set(['beach', 'roof', 'rooftop', 'island', 'moon', 'boat', 'ship', 'train', 'bus', 'street', 'corner', 'mountain', 'hill', 'stage', 'farm', 'lake', 'porch', 'balcony', 'bridge', 'sofa', 'couch', 'coast', 'mars', 'pier', 'deck', 'plane']);
const AT_PLACES = new Set(['stop', 'station', 'airport', 'party', 'desk', 'table', 'door', 'counter', 'window', 'wedding', 'funeral', 'concert', 'game', 'match', 'fair', 'zoo', 'museum', 'gym', 'bar', 'cafe', 'café', 'diner', 'work', 'home', 'sea', 'laundromat', 'mall', 'market', 'shop', 'store', 'bakery', 'dentist', 'doctor', 'office']);

/** The place in a where, for the film's `somewhere`: "on top of a mountain" → "the top of a mountain", "in my kitchen" → "my kitchen". */
export function placeOf(where: string): string {
  const w = where.trim().replace(/^on top of\s+/i, 'the top of ').replace(/^(in|at|on|inside|by|near|under|beside)\s+/i, '');
  return w || where.trim();
}

/** One word of theirs not said twice where the sentence already says it: "the summer we" + "we learned to swim". */
function dedupe(before: string, part: string): string {
  const last = before.trim().split(/\s+/).pop()?.toLowerCase();
  const [first, ...rest] = part.split(/\s+/);
  return last && rest.length && first.toLowerCase() === last ? rest.join(' ') : part;
}

/** A where as the sentence says it: "In my kitchen" → "in my kitchen", "The beach" → "the beach"; names stay as written. */
const lowerWhere = (p: string) => (LEADS.test(p) ? p.charAt(0).toLowerCase() + p.slice(1) : lower(p));

/** One blank as the sentence says it: the little word put in front of a where (`lead`), then their words. */
export interface SaidPart { slot: RollSlot; lead: string; text: string }

/**
 * The sentence in its pieces, each blank as the sentence says it: a written
 * where with its little word in front ("in Brooklyn"), no word said twice. The
 * caption on the postcard (components/IdeaRoll.tsx) draws these, so what they
 * see as they type is the sentence the crew will draw. An empty blank stays
 * empty.
 */
export function sentenceSaid(w: RollWords, shape: RollShape = shapeById()): (string | SaidPart)[] {
  let said = '';
  return sentenceParts(shape).map((p) => {
    if (p !== 'who' && p !== 'where' && p !== 'doing') { said += p; return p; }
    let lead = '';
    let text = lower(w[p].trim());
    if (p === 'where' && text) {
      const full = lowerWhere(leadWhere(text, shape));
      if (full !== text && full.endsWith(` ${text}`)) lead = full.slice(0, full.length - text.length);
      else text = full;
    }
    if (!lead) text = dedupe(said, text);
    said += lead + text;
    return { slot: p, lead, text };
  });
}

/** The sentence: "Me and Grandma Ruth talking about ghosts we have met every Friday night". */
export function rollSentence(w: RollWords, shape: RollShape = shapeById()): string {
  return sentenceSaid(w, shape).map((p) => (typeof p === 'string' ? p : p.lead + p.text)).join('').replace(/\s+/g, ' ').replace(/\s+,/g, ',').trim();
}

/** The print's words, with you in them (the backend's rollIdea says the same): "You and Grandma Ruth …", "Someone and …" without a photo. */
export function rollPrint(w: RollWords, photo: boolean, shape: RollShape = shapeById()): string {
  return rollSentence(w, shape).replace(/^Me\b/, photo ? 'You' : 'Someone');
}
